/**
 * Shared helpers for CSV/PDF blob downloads.
 *
 * - `parseFilenameFromHeader` reads RFC 5987 `filename*=UTF-8''…` first, then plain `filename=…`.
 * - `downloadBlob` triggers a synthetic <a download> click and revokes the blob URL.
 * - `formatYmd` produces a `YYYYMMDD` string for filename suffixes.
 * - `messageFromBlobError` reads the server's message back out of a FAILED blob request.
 */

import { formatDateYmd } from './dateFormat';

const BLOB_URL_REVOKE_DELAY_MS = 10_000;

export function parseFilenameFromHeader(value: string | undefined | null): string | null {
  if (!value) return null;

  const utf8Match = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(value);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch {
      /* fall through */
    }
  }

  const plainMatch = /filename\s*=\s*"?([^";]+)"?/i.exec(value);
  if (plainMatch) {
    return plainMatch[1].trim();
  }
  return null;
}

export function formatYmd(d: Date = new Date()): string {
  return formatDateYmd(d, '');
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_REVOKE_DELAY_MS);
}

/**
 * The server's own message out of a FAILED `responseType: 'blob'` request, or null.
 *
 * 🔴 WHY THE BLOB HAS TO BE READ BACK. `responseType: 'blob'` applies to the ERROR response too, so
 * `error.response.data` is a Blob holding JSON and the usual `data.message` is undefined. A caller that
 * does not do this can only show a generic line — which is how 通知センター's CSV buttons ended up either
 * silent or saying the same thing for every refusal, while the server was sending four distinct sentences
 * that differ in what the reader should DO next (wait for tonight's reconcile vs. call support).
 *
 * BOTH BODY SHAPES carry `message`: BaseApiController::errorResponse sends {success:false, message}, and
 * Laravel's abort(404, '…') sends {message}.
 *
 * NULL RATHER THAN A GUESS when the payload is anything unexpected — a network failure with no response, a
 * proxy's HTML error page, a body that is not JSON. The caller owns the generic fallback; inventing one
 * here would put a server refusal in front of somebody whose request never reached the server.
 */
export async function messageFromBlobError(error: unknown): Promise<string | null> {
  const data = (error as { response?: { data?: unknown } })?.response?.data;

  try {
    const text = data instanceof Blob ? await data.text() : typeof data === 'string' ? data : null;
    if (text === null) {
      // Not a blob request after all — axios already parsed it.
      const message = (data as { message?: unknown } | undefined)?.message;
      return typeof message === 'string' && message !== '' ? message : null;
    }

    const message = (JSON.parse(text) as { message?: unknown }).message;
    return typeof message === 'string' && message !== '' ? message : null;
  } catch {
    return null;
  }
}
